import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  CreditCard,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Copy,
  ExternalLink,
  ShieldCheck,
  Upload,
  Trash2,
  ShieldQuestion,
  XCircle,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Agency-only "My Invoices" page. Lists saved self-pay invoices with
 * download PDF + pay-now controls. Non-agency users are redirected home.
 */
const STATUS_META = {
  pending_approval: {
    label: "Pending Approval",
    icon: ShieldQuestion,
    cls: "bg-brand-teal/15 text-brand-teal border-brand-teal/40",
  },
  awaiting_payment: {
    label: "Awaiting Payment",
    icon: Clock,
    cls: "bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40",
  },
  awaiting_po: {
    label: "Awaiting PO",
    icon: Clock,
    cls: "bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40",
  },
  paid: {
    label: "Paid",
    icon: CheckCircle2,
    cls: "bg-brand-green/15 text-brand-green border-brand-green/40",
  },
  draft: {
    label: "Draft",
    icon: FileText,
    cls: "bg-brand-charcoal/10 text-brand-charcoal border-brand-charcoal/30",
  },
  rejected: {
    label: "Rejected",
    icon: XCircle,
    cls: "bg-red-50 text-red-700 border-red-200",
  },
  cancelled: {
    label: "Cancelled",
    icon: AlertCircle,
    cls: "bg-red-50 text-red-700 border-red-200",
  },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "MMM d, yyyy");
  } catch {
    return iso;
  }
};

const formatMoney = (n) =>
  typeof n === "number" ? `$${n.toFixed(2)}` : "$0.00";

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.awaiting_payment;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] ${meta.cls}`}
      data-testid={`invoice-status-${status || "unknown"}`}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
};

const InvoiceRow = ({ invoice, onDownload, onCopyLink, downloading, onApprove, onReject, onSubmitPO, actioning }) => {
  const payUrl = `/invoice/${invoice.share_token}`;
  const canPay = invoice.status === "awaiting_payment";
  const awaitingPO = invoice.status === "awaiting_po";
  const needsApproval = invoice.status === "pending_approval";
  const isRejected = invoice.status === "rejected";
  const rowActioning = actioning === invoice.id;

  return (
    <li
      data-testid={`invoice-row-${invoice.id}`}
      className={`rounded-2xl bg-white border p-5 sm:p-6 transition-all ${
        needsApproval
          ? "border-brand-teal/50 shadow-[0_10px_28px_-16px_rgba(0,166,178,0.45)]"
          : isRejected
          ? "border-red-200"
          : "border-black/5 hover:border-brand-teal/40 hover:shadow-[0_10px_28px_-16px_rgba(0,166,178,0.35)]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-black text-[15px] text-brand-charcoal tabular-nums tracking-tight">
              {invoice.invoice_number || "Pending #"}
            </p>
            <StatusBadge status={invoice.status} />
            <span data-testid={`invoice-pricing-${invoice.id}`} className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/50">
              {invoice.pricing_tier === "agency" ? "Agency pricing" : "Retail pricing"}
            </span>
          </div>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/60">
            Created {formatDate(invoice.created_at)}
            {invoice.event_date && (
              <>
                {" · "}
                Event {formatDate(invoice.event_date)}
                {invoice.event_time && ` @ ${invoice.event_time}`}
              </>
            )}
            {" · "}
            {invoice.items_count} item{invoice.items_count === 1 ? "" : "s"}
          </p>
        </div>
        <p
          data-testid={`invoice-total-${invoice.id}`}
          className="font-display font-black text-[20px] text-brand-charcoal tabular-nums"
        >
          {formatMoney(invoice.total)}
        </p>
      </div>

      {needsApproval && (
        <div
          data-testid={`invoice-approval-banner-${invoice.id}`}
          className="mt-4 rounded-xl bg-brand-teal/8 border border-brand-teal/25 px-4 py-3"
        >
          <p className="font-bold text-brand-teal uppercase tracking-[0.14em] text-[10.5px]">
            Your approval is needed
          </p>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/75 leading-snug">
            Fresh Fast Foods drafted this invoice for your team&apos;s review.
            Approve to unlock the payment link, or Reject with a note if changes
            are needed.
          </p>
        </div>
      )}

      {isRejected && invoice.rejection_reason && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
          <p className="font-bold text-red-700 uppercase tracking-[0.14em] text-[10.5px]">
            Rejected
          </p>
          <p className="mt-1 text-[12.5px] text-red-800 leading-snug">
            Your note: <span className="italic">&quot;{invoice.rejection_reason}&quot;</span>
          </p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {needsApproval && (
          <>
            <button
              type="button"
              onClick={() => onApprove(invoice)}
              disabled={rowActioning}
              data-testid={`invoice-approve-${invoice.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal/85 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {rowActioning ? "Working…" : "Approve Invoice"}
            </button>
            <button
              type="button"
              onClick={() => onReject(invoice)}
              disabled={rowActioning}
              data-testid={`invoice-reject-${invoice.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" />
              Reject
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => onDownload(invoice)}
          disabled={downloading === invoice.id}
          data-testid={`invoice-download-${invoice.id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          {downloading === invoice.id ? "Downloading…" : "Download PDF"}
        </button>

        {awaitingPO && (
          <button
            type="button"
            onClick={() => onSubmitPO(invoice)}
            disabled={rowActioning}
            data-testid={`invoice-submit-po-${invoice.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-white hover:shadow-md transition-all disabled:opacity-50"
          >
            <FileText className="h-3.5 w-3.5" />
            {rowActioning ? "Submitting…" : "Submit PO"}
          </button>
        )}

        {canPay && (
          <a
            href={payUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`invoice-pay-${invoice.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-white hover:shadow-md transition-all"
          >
            <CreditCard className="h-3.5 w-3.5" />
            Pay Now
            <ExternalLink className="h-3 w-3" />
          </a>
        )}

        {canPay && (
          <button
            type="button"
            onClick={() => onCopyLink(invoice)}
            data-testid={`invoice-copy-link-${invoice.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
          >
            <Copy className="h-3.5 w-3.5" />
            Copy Pay Link
          </button>
        )}

        {invoice.paid_at && (
          <span className="text-[11px] text-brand-charcoal/55">
            Paid {formatDate(invoice.paid_at)}
            {invoice.payment_method && ` · ${invoice.payment_method}`}
          </span>
        )}
      </div>
    </li>
  );
};

const QuoteRow = ({ quote, onCancel, actioning }) => {
  const rowActioning = actioning === quote.id;
  const status = (quote.status || "new").toLowerCase();
  const isCancelled = status === "cancelled";
  const isConverted = status === "converted" || status === "invoiced";
  const canCancel = !isCancelled && !isConverted;
  const itemCount = (quote.items || []).length;
  return (
    <li
      data-testid={`quote-row-${quote.id}`}
      className={`rounded-2xl bg-white border p-5 sm:p-6 transition-all ${
        isCancelled
          ? "border-black/10 opacity-70"
          : "border-brand-gold/40 shadow-[0_10px_28px_-16px_rgba(212,159,66,0.35)]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-black text-[15px] text-brand-charcoal tabular-nums tracking-tight">
              Quote #{quote.id?.slice(0, 8)}
            </p>
            {isCancelled ? (
              <span
                data-testid={`quote-status-cancelled-${quote.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] bg-red-50 text-red-700 border-red-200"
              >
                <XCircle className="h-3 w-3" />
                Cancelled
              </span>
            ) : (
              <span
                data-testid={`quote-status-pending-${quote.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] bg-brand-gold/15 text-brand-gold-dark border-brand-gold/40"
              >
                <ShieldQuestion className="h-3 w-3" />
                Quote Pending Admin
              </span>
            )}
          </div>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/60">
            Submitted {formatDate(quote.created_at)}
            {quote.event_date && (
              <>
                {" · "}
                Event {formatDate(quote.event_date)}
                {quote.event_time && ` @ ${quote.event_time}`}
              </>
            )}
            {itemCount > 0 && (
              <>
                {" · "}
                {itemCount} item{itemCount === 1 ? "" : "s"}
              </>
            )}
          </p>
        </div>
        <p
          data-testid={`quote-total-${quote.id}`}
          className="font-display font-black text-[20px] text-brand-charcoal tabular-nums"
        >
          {formatMoney(quote.total)}
        </p>
      </div>

      {!isCancelled && (
        <div
          data-testid={`quote-pending-banner-${quote.id}`}
          className="mt-4 rounded-xl bg-brand-gold/10 border border-brand-gold/25 px-4 py-3"
        >
          <p className="font-bold text-brand-gold-dark uppercase tracking-[0.14em] text-[10.5px]">
            Awaiting Fresh Fast Foods review
          </p>
          <p className="mt-1 text-[12.5px] text-brand-charcoal/75 leading-snug">
            Our catering team is preparing a formal invoice with the correct
            delivery fee. You&apos;ll get an email as soon as it&apos;s ready to
            approve here.
          </p>
        </div>
      )}

      {canCancel && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onCancel(quote)}
            disabled={rowActioning}
            data-testid={`quote-cancel-${quote.id}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <XCircle className="h-3.5 w-3.5" />
            {rowActioning ? "Working…" : "Cancel Quote"}
          </button>
        </div>
      )}
    </li>
  );
};

export const AccountInvoicesPage = () => {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const justSavedId = params.get("just_saved");

  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [actioning, setActioning] = useState(null);
  const [toast, setToast] = useState(null);
  const [taxExempt, setTaxExempt] = useState(null);
  const [uploadingTE, setUploadingTE] = useState(false);
  const fileInputRef = useRef(null);

  const isAgency = user && (user.role === "agency" || user.role === "admin");

  const loadAll = async () => {
    try {
      const [invRes, teRes] = await Promise.all([
        api.get("/agency/invoices"),
        api.get("/agency/tax-exempt"),
      ]);
      setInvoices(invRes.data.items || []);
      setQuotes(invRes.data.quotes || []);
      setTaxExempt(teRes.data || null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user === null) return; // still resolving
    if (!isAgency) {
      setLoading(false);
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAgency]);

  const handleTaxExemptPick = () => {
    fileInputRef.current?.click();
  };

  const handleTaxExemptUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;
    setUploadingTE(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/agency/tax-exempt/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTaxExempt(data);
      setToast({
        kind: "ok",
        text: "Document uploaded — awaiting review (usually within one business day).",
      });
    } catch (err) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.",
      });
    } finally {
      setUploadingTE(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleTaxExemptView = async () => {
    try {
      const resp = await api.get("/agency/tax-exempt/doc", { responseType: "blob" });
      // Anchor-click pattern instead of window.open — browsers block
      // window.open() after an async await (popup blocker).
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not open document.",
      });
    }
  };

  const handleTaxExemptRemove = async () => {
    if (!window.confirm("Remove your tax-exempt document? You'll need to upload a new one to re-apply.")) return;
    try {
      await api.delete("/agency/tax-exempt");
      setTaxExempt({ status: null, has_document: false });
      setToast({ kind: "ok", text: "Tax-exempt document removed." });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not remove.",
      });
    }
  };

  const handleDownload = async (invoice) => {
    setDownloading(invoice.id);
    try {
      const resp = await api.get(`/agency/invoices/${invoice.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.invoice_number || invoice.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setToast({ kind: "ok", text: "PDF downloaded." });
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Download failed.",
      });
    } finally {
      setDownloading(null);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const handleCopyLink = async (invoice) => {
    const url = `${window.location.origin}/invoice/${invoice.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setToast({ kind: "ok", text: "Pay link copied to clipboard." });
    } catch {
      setToast({ kind: "error", text: "Copy failed — select the link manually." });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleApprove = async (invoice) => {
    if (!window.confirm(
      `Approve invoice ${invoice.invoice_number || ""} for ${formatMoney(invoice.total)}? ` +
      `Once approved, it becomes payable by card.`
    )) return;
    setActioning(invoice.id);
    try {
      await api.post(`/agency/invoices/${invoice.id}/approve`);
      setToast({ kind: "ok", text: "Invoice approved — you can pay it now." });
      await loadAll();
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not approve.",
      });
    } finally {
      setActioning(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  const handleSubmitPO = async (invoice) => {
    const po = window.prompt(
      `Submit invoice ${invoice.invoice_number || ""} (${formatMoney(invoice.total)}) as a Purchase Order?\n\n` +
      `Enter your PO number, or leave blank and send it to us later:`,
      ""
    );
    if (po === null) return;
    setActioning(invoice.id);
    try {
      await api.post(`/agency/invoices/${invoice.id}/submit-po`, { po_number: po.trim() || null });
      setToast({ kind: "ok", text: "PO submitted — your order is confirmed with us." });
      await loadAll();
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not submit PO.",
      });
    } finally {
      setActioning(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  const handleReject = async (invoice) => {
    const reason = window.prompt(
      `Reject invoice ${invoice.invoice_number || ""}?\n\n` +
      `Please add a short note explaining what needs to change (optional):`,
      ""
    );
    // Cancel button on prompt returns null; empty string is an intentional
    // "no reason" rejection which we still send through.
    if (reason === null) return;
    setActioning(invoice.id);
    try {
      await api.post(`/agency/invoices/${invoice.id}/reject`, {
        reason: reason.trim() || null,
      });
      setToast({ kind: "ok", text: "Invoice rejected — we've let the team know." });
      await loadAll();
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not reject.",
      });
    } finally {
      setActioning(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  const handleCancelQuote = async (quote) => {
    const reason = window.prompt(
      `Cancel this quote request?\n\n` +
      `Optional — tell us why so we know whether to follow up another way:`,
      ""
    );
    if (reason === null) return;
    setActioning(quote.id);
    try {
      await api.post(`/agency/quotes/${quote.id}/cancel`, {
        reason: reason.trim() || null,
      });
      setToast({ kind: "ok", text: "Quote cancelled — the team has been notified." });
      await loadAll();
    } catch (e) {
      setToast({
        kind: "error",
        text: formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel.",
      });
    } finally {
      setActioning(null);
      setTimeout(() => setToast(null), 3500);
    }
  };

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        <section className="pt-24 sm:pt-28 lg:pt-32 pb-8 bg-brand-cream">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            <Link
              to="/menu"
              data-testid="account-back-to-menu"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal hover:bg-brand-charcoal hover:text-white transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Menu
            </Link>
            <p className="mt-6 text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
              Agency Account · Saved Invoices
            </p>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight text-brand-charcoal leading-[1.05]">
              My Invoices<span className="text-brand-gold">.</span>
            </h1>
            <p className="mt-3 text-[14px] text-brand-charcoal/65 max-w-2xl leading-relaxed">
              Download any invoice as a PDF for internal approval. Agency-priced
              invoices are settled by tapping <strong>Submit PO</strong> once approved;
              card-approved invoices can be paid online when you&apos;re ready.
            </p>
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            {toast && (
              <div
                data-testid="account-invoices-toast"
                className={`mb-5 rounded-2xl px-4 py-3 text-[13px] border ${
                  toast.kind === "ok"
                    ? "bg-brand-teal/8 border-brand-teal/25 text-brand-teal"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                {toast.text}
              </div>
            )}

            {justSavedId && (
              <div
                data-testid="account-invoices-just-saved"
                className="mb-5 rounded-2xl bg-brand-gold/15 border border-brand-gold/40 px-4 py-3.5 text-[13px] text-brand-charcoal leading-snug"
              >
                <p className="font-bold text-brand-gold-dark uppercase tracking-[0.14em] text-[10.5px]">
                  Invoice saved
                </p>
                <p className="mt-1">
                  Your invoice is ready below. We&apos;ve emailed a copy to your
                  address — forward it for approval, then pay when you&apos;re ready.
                </p>
              </div>
            )}

            {/* Tax-exempt card. Renders 4 distinct states:
                  · none  — invite to upload
                  · pending — awaiting admin review
                  · approved — badge + downloadable doc
                  · rejected — reason + re-upload CTA
                Once approved, all NEW invoices for this user auto-apply
                tax=$0 and existing awaiting-payment invoices are recomputed
                server-side. */}
            {user && isAgency && taxExempt && (
              <div
                data-testid="tax-exempt-card"
                className={`mb-5 rounded-3xl border-2 p-5 sm:p-6 transition-colors ${
                  taxExempt.status === "approved"
                    ? "bg-brand-green/6 border-brand-green/40"
                    : taxExempt.status === "pending"
                    ? "bg-brand-gold/10 border-brand-gold/40"
                    : taxExempt.status === "rejected"
                    ? "bg-red-50 border-red-200"
                    : "bg-white border-brand-teal/30"
                }`}
              >
                <div className="flex items-start gap-4">
                  <span
                    className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                      taxExempt.status === "approved"
                        ? "bg-brand-green text-white"
                        : taxExempt.status === "pending"
                        ? "bg-brand-gold text-brand-charcoal"
                        : taxExempt.status === "rejected"
                        ? "bg-red-500 text-white"
                        : "bg-brand-teal text-white"
                    }`}
                  >
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10.5px] font-black uppercase tracking-[0.14em] text-brand-charcoal/60">
                      Tax-Exempt Status
                    </p>
                    <h2
                      data-testid="tax-exempt-status-label"
                      className="mt-1 font-display font-black text-[18px] tracking-tight text-brand-charcoal"
                    >
                      {taxExempt.status === "approved" && "Approved — invoices are tax-free."}
                      {taxExempt.status === "pending" && "Under review."}
                      {taxExempt.status === "rejected" && "Not currently approved."}
                      {!taxExempt.status && "Are you tax-exempt?"}
                    </h2>
                    <p className="mt-1.5 text-[12.5px] text-brand-charcoal/65 leading-snug">
                      {taxExempt.status === "approved" && (
                        <>
                          Reviewed {taxExempt.reviewed_at?.slice(0, 10)}. Every new
                          invoice on your account will automatically have $0 tax
                          applied. Any awaiting invoices have been recalculated.
                        </>
                      )}
                      {taxExempt.status === "pending" && (
                        <>
                          Your certificate <b>{taxExempt.filename}</b> was submitted{" "}
                          {taxExempt.submitted_at?.slice(0, 10)}. Our team reviews
                          within one business day — you&apos;ll get an email once
                          decided.
                        </>
                      )}
                      {taxExempt.status === "rejected" && (
                        <>
                          {taxExempt.reject_reason ||
                            "Please re-upload an updated certificate or reply to your rejection email with questions."}
                        </>
                      )}
                      {!taxExempt.status && (
                        <>
                          Upload your W-9 / Tax-Exempt Certificate (PDF or image).
                          Once our team approves it, sales tax on every invoice
                          drops to $0.
                        </>
                      )}
                    </p>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {(!taxExempt.status || taxExempt.status === "rejected") && (
                        <button
                          type="button"
                          data-testid="tax-exempt-upload-btn"
                          onClick={handleTaxExemptPick}
                          disabled={uploadingTE}
                          className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-charcoal transition-colors disabled:opacity-50"
                        >
                          <Upload className="h-3.5 w-3.5" />
                          {uploadingTE ? "Uploading…" : "Upload Certificate"}
                        </button>
                      )}
                      {taxExempt.status === "pending" && (
                        <>
                          <button
                            type="button"
                            data-testid="tax-exempt-replace-btn"
                            onClick={handleTaxExemptPick}
                            disabled={uploadingTE}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors disabled:opacity-50"
                          >
                            <Upload className="h-3.5 w-3.5" />
                            Replace file
                          </button>
                          <button
                            type="button"
                            data-testid="tax-exempt-remove-btn"
                            onClick={handleTaxExemptRemove}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </>
                      )}
                      {taxExempt.status === "approved" && taxExempt.has_document && (
                        <>
                          <button
                            type="button"
                            onClick={handleTaxExemptView}
                            data-testid="tax-exempt-view-doc"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            View my certificate
                          </button>
                          <button
                            type="button"
                            data-testid="tax-exempt-replace-btn"
                            onClick={handleTaxExemptPick}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
                          >
                            <Upload className="h-3.5 w-3.5" />
                            Replace certificate
                          </button>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*"
                      onChange={handleTaxExemptUpload}
                      className="hidden"
                      data-testid="tax-exempt-file-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {!user && (
              <div
                data-testid="account-invoices-signin-required"
                className="rounded-3xl bg-brand-cream/60 border border-black/5 p-8 text-center"
              >
                <FileText className="h-8 w-8 mx-auto text-brand-charcoal/40" />
                <p className="mt-3 font-display font-black text-[18px] tracking-tight">
                  Sign in to view your invoices
                </p>
                <p className="mt-2 text-[13px] text-brand-charcoal/65 max-w-md mx-auto">
                  This page is for logged-in agency accounts.
                </p>
                <Link
                  to="/agency/login"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                >
                  Sign In
                </Link>
              </div>
            )}

            {user && !isAgency && (
              <div
                data-testid="account-invoices-agency-only"
                className="rounded-3xl bg-brand-cream/60 border border-black/5 p-8 text-center"
              >
                <AlertCircle className="h-8 w-8 mx-auto text-brand-gold-dark" />
                <p className="mt-3 font-display font-black text-[18px] tracking-tight">
                  Available for agency accounts only
                </p>
                <p className="mt-2 text-[13px] text-brand-charcoal/65 max-w-md mx-auto">
                  The save-invoice-for-later flow is currently offered to
                  approved agency clients. Private buyers can check out directly
                  from the cart.
                </p>
              </div>
            )}

            {user && isAgency && loading && (
              <p data-testid="account-invoices-loading" className="text-[14px] text-brand-charcoal/55">
                Loading your invoices…
              </p>
            )}

            {user && isAgency && !loading && error && (
              <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-[13px]">
                {error}
              </div>
            )}

            {user && isAgency && !loading && !error && invoices.length === 0 && quotes.length === 0 && (
              <div
                data-testid="account-invoices-empty"
                className="rounded-3xl bg-brand-cream/60 border border-black/5 p-8 text-center"
              >
                <FileText className="h-8 w-8 mx-auto text-brand-charcoal/40" />
                <p className="mt-3 font-display font-black text-[18px] tracking-tight">
                  No saved invoices yet
                </p>
                <p className="mt-2 text-[13px] text-brand-charcoal/65 max-w-md mx-auto">
                  Build a cart and tap <b>&quot;Save Invoice — Pay Later&quot;</b> on the
                  cart page to generate an approval-ready PDF.
                </p>
                <Link
                  to="/menu"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                >
                  Browse Menu
                </Link>
              </div>
            )}

            {user && isAgency && !loading && (invoices.length > 0 || quotes.length > 0) && (
              <ul
                data-testid="account-invoices-list"
                className="space-y-3"
              >
                {quotes.map((q) => (
                  <QuoteRow
                    key={`q-${q.id}`}
                    quote={q}
                    onCancel={handleCancelQuote}
                    actioning={actioning}
                  />
                ))}
                {invoices.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    invoice={inv}
                    onDownload={handleDownload}
                    onCopyLink={handleCopyLink}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onSubmitPO={handleSubmitPO}
                    downloading={downloading}
                    actioning={actioning}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default AccountInvoicesPage;
